import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { Observable, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { RevenueEstablishment } from '../../combined-reports/combined-revenue-overview/model/revenue.establishment';
import { ENDPOINTS } from '../../constants';
import { AverageRevenueCustomers } from '../../dashboard/models/average-revenue-customers';

import { AverageThroughputTime } from '../../dashboard/models/average-throughput-time';
import { CompanyData } from '../../settings/model/company-data.model';
import { CompanySettings } from '../../settings/model/company-settings';
import { DateRange } from '../components/datepicker/calendar/DateRange';
import { RestService } from '../http/rest.service';
import { Report } from './model/report.model';
import { REPORTROUTES } from './report-routes.constants';
import { IntervalReportFilter } from './reports.filters';
import { Store } from '@ngrx/store';
import * as fromCompanySettingsStore from '../../settings/store';
import { RpcService } from '../http/rpc.service';

export interface ReportingEndPointsResponse {
  reportingPosServerUrl: string | null;
  reportingUrl: string | null;
}

@Injectable()
export class ReportsService {
  constructor(
    private restService: RestService,
    private store: Store<fromCompanySettingsStore.CompanyState>,
    private rpcService: RpcService
  ) {}

  public getIntervalReport(
    filter: IntervalReportFilter,
    requestedItems: Array<string>
  ): Observable<any> {
    filter.requestedItems = requestedItems || [];
    return fromCompanySettingsStore.getSettingsFromStore(this.store).pipe(
      switchMap((companySettings: CompanySettings) => {
        const defaultStartDate: moment.Moment = moment(filter.from)
          .startOf('day')
          .minute(companySettings.openingTime / 1000 / 60)
          .utc();
        const startOfDay: number =
          defaultStartDate.hour() + defaultStartDate.minute() / 60;
        filter.intervals = {
          timezone: companySettings.timeZoneId,
          startOfDay: startOfDay,
          startOfMonth: 1,
          startOfWeek: 1
        };
        return this.getReport(filter);
      })
    );
  }

  public getReportEndPoints(): Observable<ReportingEndPointsResponse> {
    return this.rpcService.makeManagerCall('getEndpoints');
  }

  public getAverageThroughputTime(
    dateRange: DateRange
  ): Observable<AverageThroughputTime> {
    return this.restService
      .query(ENDPOINTS.report, REPORTROUTES.averageThroughputTime, {
        method: 'post',
        body: {
          from: dateRange.start,
          to: dateRange.end
        }
      })
      .pipe(
        map(this.checkErrorResponse),
        map((response: any) => {
          return AverageThroughputTime.fromDTO(response);
        }),
        catchError(this.errorCatch)
      );
  }

  public getRevenueEstablishment(
    dateRange: DateRange,
    companyData: CompanyData
  ): Observable<RevenueEstablishment> {
    const requestedItems: Array<string> = ['totals', 'revenue'];

    return this.restService
      .query(ENDPOINTS.report, REPORTROUTES.intervalReport, {
        method: 'post',
        body: {
          from: dateRange.start,
          to: dateRange.end,
          requestedItems: requestedItems
        },
        context: companyData.context,
        token: companyData.token
      })
      .pipe(
        map(this.checkErrorResponse),
        map((response: any) => {
          return RevenueEstablishment.fromDTO(response, companyData);
        }),
        catchError(this.errorCatch)
      );
  }

  public getAverageRevenueCustomersPerHour(
    dateRange: DateRange
  ): Observable<AverageRevenueCustomers> {
    return this.getAverageRevenueCustomers(
      dateRange,
      REPORTROUTES.revenueCustomerHour
    );
  }

  public getAverageRevenueCustomersPerDay(
    dateRange: DateRange
  ): Observable<AverageRevenueCustomers> {
    return this.getAverageRevenueCustomers(
      dateRange,
      REPORTROUTES.revenueCustomerDay
    );
  }

  private getAverageRevenueCustomers(
    dateRange: DateRange,
    api: string
  ): Observable<AverageRevenueCustomers> {
    return this.restService
      .query(ENDPOINTS.report, api, {
        method: 'post',
        body: {
          from: dateRange.start,
          to: dateRange.end
        }
      })
      .pipe(
        map(this.checkErrorResponse),
        map((response: any) => {
          return AverageRevenueCustomers.fromDTO(response);
        }),
        catchError(this.errorCatch)
      );
  }

  private checkErrorResponse(response: any): any {
    if (typeof response['error'] !== 'undefined') {
      throw response;
    }
    return response;
  }

  private errorCatch(error: any): Observable<any> {
    return throwError(error.msg || error.error);
  }

  private getReport(filter: IntervalReportFilter): Observable<any> {
    return this.restService
      .query(ENDPOINTS.report, REPORTROUTES.intervalReport, {
        method: 'post',
        body: filter
      })
      .pipe(
        map(this.checkErrorResponse),
        map((response: any) => Report.fromDTO(response)),
        catchError(this.errorCatch)
      );
  }
}
